import { RefreshCw, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { LimitExceededError } from '../../lib/api'
import { LimitExceededBanner } from '../ui/LimitExceededBanner'
import { useAiNarrative } from '../../hooks/useAiNarrative'

function NarrativeSkeleton() {
  return (
    <div className="space-y-2.5" aria-label="Caricamento narrativa AI">
      {[100, 83, 66, 100, 75].map((w, i) => (
        <div
          key={i}
          className="h-2.5 rounded-full animate-shimmer"
          style={{ width: `${w}%` }}
        />
      ))}
    </div>
  )
}

function NarrativeError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-start gap-3">
      <p className="text-sm text-[--km-text-muted]">
        Impossibile generare la narrativa AI. Riprovare.
      </p>
      <button
        onClick={onRetry}
        className="flex items-center gap-1.5 rounded-xl bg-white/[0.05] px-3 py-1.5 text-xs font-medium text-[--km-text-secondary] hover:bg-white/[0.08] transition-colors"
      >
        <RefreshCw size={11} />
        Riprova
      </button>
    </div>
  )
}

export function NarrativeCard() {
  const [forceRefresh, setForceRefresh] = useState(false)
  const { data, isLoading, error, refetch } = useAiNarrative(forceRefresh)

  function handleRefresh() {
    setForceRefresh(true)
    refetch().finally(() => setForceRefresh(false))
  }

  return (
    <div className="flex h-full flex-col rounded-2xl border border-white/[0.07] bg-[#0d1321]/70 backdrop-blur-sm p-5 shadow-[0_4px_24px_rgba(0,0,0,0.4)]">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="flex h-7 w-7 items-center justify-center rounded-xl gradient-brand shadow-[0_0_12px_rgba(99,102,241,0.4)]">
            <Sparkles size={13} className="text-white" />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-[--km-text-secondary]">
              AI Narrative
            </span>
            <div className="text-[9px] text-indigo-400 font-semibold tracking-wider uppercase mt-0.5">
              Claude AI
            </div>
          </div>
        </div>

        {data && (
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            aria-label="Rigenera narrativa AI"
            className="flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-xs font-medium text-[--km-text-muted] hover:bg-white/[0.05] hover:text-[--km-text-secondary] disabled:opacity-40 transition-colors"
          >
            <RefreshCw size={11} className={isLoading ? 'animate-spin' : ''} />
            Aggiorna
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && <NarrativeSkeleton />}

        {!isLoading && error && (
          error instanceof LimitExceededError
            ? <LimitExceededBanner error={error} />
            : <NarrativeError onRetry={handleRefresh} />
        )}

        {!isLoading && data && (
          <p className="text-sm leading-relaxed text-[--km-text-secondary] whitespace-pre-line">
            {data.content}
          </p>
        )}
      </div>

      {/* Footer meta */}
      {data && !isLoading && (
        <div className="mt-4 flex items-center justify-between border-t border-white/[0.05] pt-3">
          <span className="text-[10px] text-[--km-text-muted] font-medium">{data.model}</span>
          <span className="text-[10px] text-[--km-text-muted]">
            {data.input_tokens + data.output_tokens} token
          </span>
        </div>
      )}
    </div>
  )
}
